import { useState } from "react";
import { motion } from "framer-motion";
import { springs } from "@/components/ui/motion";
import { useTranslation } from "@/packages/utils";
import type { SeverityType } from "@/packages/domains/findings";
import { useSeverityConfig } from "./findings-constants";

// ─── Types ───────────────────────────────────────────────────────────────────

interface FindingLike {
  severity: SeverityType;
  status: string;
}

interface FindingsHeatmapStripProps {
  findings: FindingLike[];
}

interface SegmentData {
  severity: SeverityType;
  count: number;
  percentage: number;
  label: string;
  color: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const SEVERITY_ORDER: SeverityType[] = [
  "critical",
  "major",
  "minor",
  "observation",
];

// ─── Component ───────────────────────────────────────────────────────────────

export function FindingsHeatmapStrip({ findings }: FindingsHeatmapStripProps) {
  const [hoveredSeverity, setHoveredSeverity] = useState<string | null>(null);
  const { t } = useTranslation("audits");
  const severityConfig = useSeverityConfig();
  const total = findings.length;
  const resolvedCount = findings.filter((f) => f.status === "resolved").length;

  const segments: SegmentData[] = SEVERITY_ORDER.map((severity) => {
    const count = findings.filter(
      (f) => f.severity === severity && f.status !== "resolved",
    ).length;
    const config = severityConfig[severity];
    return {
      severity,
      count,
      percentage: total > 0 ? (count / total) * 100 : 0,
      label: config.label,
      color: config.dot,
    };
  }).filter((s) => s.count > 0);

  if (total === 0) return null;

  return (
    <motion.div
      // className="mb-4"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springs.snappy}
    >
      <div className="flex items-center gap-4">
        {/* Heatmap bar */}
        <div className="relative flex-1">
          <div className="flex h-2.5 overflow-hidden rounded-full">
            {segments.map((segment) => (
              <div
                key={segment.severity}
                role="img"
                aria-label={t("findings.heatmap.severityTooltip", {
                  count: segment.count,
                  severity: segment.label,
                })}
                className="relative cursor-default transition-opacity"
                style={{
                  width: `${segment.percentage}%`,
                  backgroundColor: segment.color,
                  opacity: hoveredSeverity
                    ? hoveredSeverity === segment.severity
                      ? 1
                      : 0.4
                    : 1,
                }}
                onMouseEnter={() => setHoveredSeverity(segment.severity)}
                onMouseLeave={() => setHoveredSeverity(null)}
              >
                {/* Tooltip */}
                {hoveredSeverity === segment.severity && (
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-primary px-2 py-1 text-[11px] font-medium text-white shadow-lg z-10">
                    {t("findings.heatmap.severityTooltip", {
                      count: segment.count,
                      severity: segment.label,
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Count labels below bar */}
          <div className="mt-1 flex">
            {segments.map((segment) => (
              <div
                key={segment.severity}
                className="text-center text-[11px] font-medium"
                style={{
                  width: `${segment.percentage}%`,
                  color: segment.color,
                }}
              >
                {segment.count}
              </div>
            ))}
          </div>
        </div>

        {/* Resolved fraction */}
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="text-[12px] font-medium text-secondary">
            {t("findings.heatmap.resolvedCount", {
              resolved: resolvedCount,
              total,
            })}
          </span>
          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-card">
            <motion.div
              className="h-full rounded-full bg-accent"
              initial={{ width: 0 }}
              animate={{
                width: total > 0 ? `${(resolvedCount / total) * 100}%` : "0%",
              }}
              transition={{ ...springs.smooth, delay: 0.2 }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
}
